import { useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { delayForIndexing } from "@examspark/common";
import {
  dateRange,
  fromAWSDate,
  toAWSDate,
} from "@examspark/common/dist/utils/coursePlanner";
import {
  useAppContext,
  useMutationCreateCourseCalendar,
} from "@examspark/common-app";
import { toast } from "@examspark/common-ui";
import { useArrayReducer } from "@examspark/common-ui/dist/hooks/useArray";
import {
  AcademicCalendar,
  Course,
  NonInstructionDate,
  ResourceMaturity,
  ResourceVisibilityType,
  UnitPlan,
} from "@examspark/types";

import { getUrlForObject } from "../../../utils";

import { CourseCalendarForm } from "./CourseCalendarForm";

export const CreateCourseCalendar = () => {
  const [course, setCourse] = useState<Course>();
  const [calendar, setCalendar] = useState<AcademicCalendar>();
  const [name, setName] = useState<string>();
  const holidays = useArrayReducer<NonInstructionDate & { id: string }>([]);
  const [plan, setPlan] = useState<UnitPlan[]>();
  const visibility = useState<ResourceVisibilityType | null>(
    ResourceVisibilityType.WorkspaceShareable,
  );

  const [context] = useAppContext();
  const navigate = useNavigate();

  const createMutation = useMutationCreateCourseCalendar();
  const queryClient = useQueryClient();

  // do some recalculating to account for additional non-instructional days.
  // these will get materialized into the unitPlan so we don't have to worry
  // about them later.
  const fullPlan = plan?.map((unit) => ({
    ...unit,
    dates: unit.dates?.filter((date) => {
      return !holidays.values
        .flatMap((holiday) =>
          holiday.endAt
            ? [
                ...dateRange(
                  fromAWSDate(holiday.startAt),
                  fromAWSDate(holiday.endAt),
                ),
              ].map(toAWSDate)
            : [holiday.startAt],
        )
        .includes(date);
    }),
  }));

  const onSave = async () => {
    const workspaceId = context.workspaceUser?.workspace?.id;
    const bankId = context.appLicenseConfig?.userContentBankId;

    if (!fullPlan || !calendar || !course || !workspaceId) {
      alert("missing required fields");
      return;
    }

    try {
      const sortedHolidays = holidays.values
        .map((holiday) => ({
          startAt: holiday.startAt,
          endAt: holiday.endAt,
          label: holiday.label,
          type: holiday.type,
        }))
        .sort(
          (a, b) =>
            a.startAt.localeCompare(b.startAt) ||
            (a.label ?? "").localeCompare(b.label ?? ""),
        );

      const res = await createMutation.mutateAsync({
        schemaVersion: "2020-01-01",
        workspaceId,

        ...(bankId ? { bankId } : {}),
        maturity: ResourceMaturity.Operational,
        visibility: visibility[0],

        name,
        courseId: course.id,
        calendarId: calendar.id,
        units: fullPlan,

        additionalNonInstructionDates: sortedHolidays,
      });

      await delayForIndexing();
      queryClient.invalidateQueries(["course-calendars"]);

      toast.success("Calendar created");

      const url = getUrlForObject(res.data?.createCourseCalendar);

      setTimeout(() => (url ? navigate(url) : null), 1000);
    } catch (err) {
      toast.error("Error creating calendar");
      console.error(err);
      return;
    }
  };

  return (
    <CourseCalendarForm
      action="Create"
      course={[course, setCourse]}
      calendar={[calendar, setCalendar]}
      name={[name, setName]}
      holidays={holidays}
      plan={[fullPlan, setPlan]}
      visibility={visibility}
      onSave={onSave}
    />
  );
};
